import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";

import { mrtSortingStateToObject } from "src/api/apiHelpers";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { PackagingReferenceUnified } from "src/api/client-api/packagingReferenceUnified/types";
import { useGetAllPackagingReferencesUnified } from "src/api/client-api/packagingReferenceUnified/useGetAllPackagingReferencesUnified";
import { PackagingList, useGetAllPackaging } from "src/api/client-api/packagings/useGetAllPackagings";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { ProductFormProductPackaging } from "src/pages/Product/ProductForm/ProductForm.types";

export type PackagingKind = PackagingReferenceUnified["kind"];
export type PackagingGridEntity = PackagingList | PackagingReferenceUnified;
export type PackagingPickerSource = "repository" | "material-list";

export type PackagingGridProps = {
    delayedSearch: string;
    existingPackagings: ProductFormProductPackaging[];
    setNewPackagings: (packagings: ProductFormProductPackaging[]) => void;
};

export type PackagingListGridCore = {
    canManagePackagings: boolean;
    isLoading: boolean;
    newPackagingHandler: (packagingList: PackagingGridEntity) => ProductFormProductPackaging;
    packagings: PackagingGridEntity[];
    pagination: MRT_PaginationState;
    selectedCompany: ReturnType<typeof useFoodpilotContext>["selectedCompany"];
    selectionHandler: (entity: ProductFormProductPackaging) => string;
    setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;
    sorting: MRT_SortingState;
    source: PackagingPickerSource;
    totalItems: number;
};

export const getPackagingKind = (packaging: PackagingGridEntity): PackagingKind | null => {
    if ("kind" in packaging) {
        return packaging.kind;
    }

    if (packaging["@type"] === "PackagingElement") {
        return "element";
    }

    if (packaging["@type"] === "PackagingComposite") {
        return "composite";
    }

    return null;
};

export const getPackagingKindIcon = (kind: PackagingKind) => (kind === "element" ? "box" : "boxes");

export const usePackagingListGridCore = (
    delayedSearch: string,
    pageSize = 6,
    requestedSource?: PackagingPickerSource,
): PackagingListGridCore => {
    const { selectedCompany } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();
    const canManagePackagings = isFeatureActivated("FS-manage-my-packagings");
    const source =
        requestedSource === "repository" && !canManagePackagings ?
            "material-list"
        :   (requestedSource ?? (canManagePackagings ? "repository" : "material-list"));

    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "name", desc: false }]);

    useEffect(() => {
        setPagination((currentPagination) =>
            currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 },
        );
    }, [delayedSearch, source]);

    const tenantPackagingsQuery = useGetAllPackaging({
        cacheTime: 0,
        enabled: source === "repository",
        params: {
            page: pagination.pageIndex + 1,
            itemsPerPage: pagination.pageSize,
            order: mrtSortingStateToObject(sorting),
        },
        scoresQuery: {
            scores: {
                subscores: false,
            },
        },
        search: delayedSearch,
    });

    const referencePackagingsQuery = useGetAllPackagingReferencesUnified({
        enabled: source === "material-list",
        page: pagination.pageIndex + 1,
        itemsPerPage: pagination.pageSize,
        q: delayedSearch,
    });

    const packagings = useMemo(
        () =>
            (source === "repository" ?
                tenantPackagingsQuery.packagings
            :   referencePackagingsQuery.packagingReferences) ?? [],
        [source, tenantPackagingsQuery.packagings, referencePackagingsQuery.packagingReferences],
    );
    const isLoading = source === "repository" ? tenantPackagingsQuery.isLoading : referencePackagingsQuery.isLoading;
    const totalItems =
        (source === "repository" ? tenantPackagingsQuery.totalItems : referencePackagingsQuery.totalItems) ?? 0;

    const getReferenceIri = (reference: PackagingReferenceUnified) =>
        reference["@id"] ?? `/api/packaging-references-unified/${reference.compositeId}`;

    const selectionHandler = (entity: ProductFormProductPackaging) => {
        if (entity.packaging) {
            return entity.packaging["@id"];
        }

        if (entity.packagingReference) {
            return (
                entity.packagingReference["@id"] ??
                `/api/packaging-references-unified/${entity.packagingReference.compositeId}`
            );
        }

        return "";
    };

    const newPackagingHandler = (packagingList: PackagingGridEntity) => {
        if (source === "material-list") {
            const reference = packagingList as PackagingReferenceUnified;
            const newPackaging: ProductFormProductPackaging = {
                packagingReference: {
                    "@id": getReferenceIri(reference),
                    compositeId: reference.compositeId,
                    id: reference.id,
                    kind: reference.kind,
                    name: reference.name,
                },
                quantity: reference.kind === "composite" ? 1 : null,
                usedWeight: reference.kind === "element" ? (reference.defaultWeight ?? null) : undefined,
            };

            return newPackaging;
        }

        const tenantPackaging = packagingList as PackagingList;
        const newPackaging: ProductFormProductPackaging = {
            packaging: {
                "@id": tenantPackaging["@id"],
                "@type": tenantPackaging["@type"],
                id: tenantPackaging.id,
                name: tenantPackaging.name,
                externalId: tenantPackaging.externalId,
                weight: tenantPackaging.weight,
            },
            quantity: 1,
        };

        return newPackaging;
    };

    return {
        canManagePackagings,
        isLoading,
        newPackagingHandler,
        packagings,
        pagination,
        selectedCompany,
        selectionHandler,
        setPagination,
        setSorting,
        sorting,
        source,
        totalItems,
    };
};
